/**
 * Ads Campaign Preview Panel (right rail)
 *
 * Visible on steps 1-3 of the wizard; hidden on the Summary step.
 */

import React, { useMemo } from "react";
import type { AdCampaignFormData } from "../../../../../api/ads/types";
import "./AdCampaignPreview.css";

const formatMoney = (value: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(value);

const formatISODateToDMY = (dateString: string) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

type AdCampaignPreviewProps = {
  formData: AdCampaignFormData;
};

const pricingRateLabel: Record<string, string> = {
  CPC: "CPC Rate",
  CPM: "CPM Rate",
  CPA: "CPA Rate",
  Flat: "Flat Monthly Rate",
};

const AdCampaignPreview: React.FC<AdCampaignPreviewProps> = ({ formData }) => {
  const rateLabel = pricingRateLabel[formData.pricingModel as string] ?? "Rate";

  const pricingRate = useMemo(() => {
    if (typeof formData.pricingRate === "number") return formatMoney(formData.pricingRate);
    return "-";
  }, [formData.pricingRate]);

  const dailyBudget = useMemo(() => {
    if (typeof formData.dailyBudget === "number") return formatMoney(formData.dailyBudget);
    return "-";
  }, [formData.dailyBudget]);

  const totalBudget = useMemo(() => {
    if (typeof formData.totalBudget === "number") return formatMoney(formData.totalBudget);
    return "-";
  }, [formData.totalBudget]);

  const validity = `${formatISODateToDMY(formData.startDate)} - ${formatISODateToDMY(formData.endDate)}`;

  return (
    <div className="ads-create-campaign-preview-card">
      <div className="ads-create-campaign-preview-card__title">Preview</div>
      <div className="ads-create-campaign-preview-card__divider" />

      <div className="ads-create-campaign-preview-row">
        <div className="ads-create-campaign-preview-row__label">Advertiser</div>
        <div className="ads-create-campaign-preview-row__value">{formData.advertiserName || "-"}</div>
      </div>

      <div className="ads-create-campaign-preview-row">
        <div className="ads-create-campaign-preview-row__label">Campaign name</div>
        <div className="ads-create-campaign-preview-row__value">{formData.campaignName || "-"}</div>
      </div>

      <div className="ads-create-campaign-preview-row">
        <div className="ads-create-campaign-preview-row__label">Pricing Model</div>
        <div className="ads-create-campaign-preview-row__value">{formData.pricingModel || "-"}</div>
      </div>

      <div className="ads-create-campaign-preview-row">
        <div className="ads-create-campaign-preview-row__label">{rateLabel}</div>
        <div className="ads-create-campaign-preview-row__value">{pricingRate}</div>
      </div>

      <div className="ads-create-campaign-preview-row">
        <div className="ads-create-campaign-preview-row__label">Daily Budget</div>
        <div className="ads-create-campaign-preview-row__value">{dailyBudget}</div>
      </div>

      <div className="ads-create-campaign-preview-row">
        <div className="ads-create-campaign-preview-row__label">Total Budget</div>
        <div className="ads-create-campaign-preview-row__value">{totalBudget}</div>
      </div>

      <div className="ads-create-campaign-preview-row">
        <div className="ads-create-campaign-preview-row__label">Validity</div>
        <div className="ads-create-campaign-preview-row__value">{validity}</div>
      </div>

      <div className="ads-create-campaign-preview-row">
        <div className="ads-create-campaign-preview-row__label">Placement</div>
        <div className="ads-create-campaign-preview-row__value">{formData.placementName || "-"}</div>
      </div>
    </div>
  );
};

export default AdCampaignPreview;

